import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Histogram from '../components/Histogram';
import Simulator from '../features/simulator/Simulator';
import { runSimulation } from '../core/app';
import { SimulationInput, SimulationMode, SimulationOutput, WeaponMatrixInput, WeaponMatrixOutput } from '../core/types';
import './App.css';
import Changelog from '../components/Changelog';
import { Constants } from '../core/constants';
import { AppTabs } from '../core/constants/constants';
import Welcome from '../components/Welcome';
import { runWeaponMatrixSimulation } from '../core/weapon-matrix';
import WeaponMatrixResults from '../features/weapon-matrix/WeaponMatrixResults';
import { isSimulationAbortedError, throwIfSimulationAborted } from '../core/simulation-control';
import Button from '../components/Button';
import SegmentedControl from '../components/SegmentedControl';
import {
    SharedArmoryImportPreview,
    clearStoredArmoryData,
    loadEffectiveArmoryFromStorage,
    migrateArmoryStorage,
    previewSharedArmoryImport,
} from '../core/utilities/armory-storage';
import { clearStoredUiState, loadAppUiState, loadSimulatorState, saveAppUiState } from '../core/utilities/ui-storage';
import { SharedLoadoutImportModal } from '../components/SharedLoadoutImportModal';
import {
    SharedLoadoutImportCandidate,
    SharedLoadoutImportRequest,
    SharedLoadoutImportMode,
    clearSharedLoadoutHash,
    getRequiredSpoilerTagsForLoadoutSnapshot,
    readSharedLoadoutFromHash,
} from '../core/utilities/loadout-sharing';
import { expandCycleSpoilerTags } from '../core/utilities/tags';

type WorkspaceView = 'simulator' | 'library';

interface SharedLoadoutImportPreview {
    armoryImportPreview: SharedArmoryImportPreview | null;
    canImportAsArmory: boolean;
    manualMissingSpoilerTags: string[];
    armoryMissingSpoilerTags: string[];
}

function normalizeActiveTab(storedActiveTab: AppTabs | null): AppTabs {
    if (storedActiveTab === null) {
        return AppTabs.ARGONAUT_LOADOUTS;
    }

    const validTabs = [AppTabs.ARGONAUT_LOADOUTS, AppTabs.LOADOUT_STATS, AppTabs.ARMORY_EDITOR];
    return validTabs.includes(storedActiveTab) ? storedActiveTab : AppTabs.ARGONAUT_LOADOUTS;
}

function normalizeSimulatorInputTab(storedTab: AppTabs | null): AppTabs {
    return storedTab === AppTabs.LOADOUT_STATS ? AppTabs.LOADOUT_STATS : AppTabs.ARGONAUT_LOADOUTS;
}

function initializeAppStorage() {
    ensureScopedStorageReset();
    const migration = migrateArmoryStorage();
    const localSchemaVersion = parseInt(localStorage.getItem(Constants.LocalStorageKeys.SCHEMA_VERSION) || '');
    let showWarning = false;
    let schemaChangesMessage = '';

    if (migration.needsResetWarning) {
        schemaChangesMessage = migration.message;
        showWarning = true;
    } else if (!isNaN(localSchemaVersion) && localSchemaVersion < Constants.CURRENT_SCHEMA_VERSION && !migration.migrated) {
        schemaChangesMessage = Constants.SCHEMA_CHANGES.slice(localSchemaVersion).join(', ');
        localStorage.setItem(Constants.LocalStorageKeys.SCHEMA_VERSION, `${Constants.CURRENT_SCHEMA_VERSION}`);
    } else {
        localStorage.setItem(Constants.LocalStorageKeys.SCHEMA_VERSION, `${Constants.CURRENT_SCHEMA_VERSION}`);
    }

    return { showWarning, schemaChangesMessage };
}

function ensureScopedStorageReset(storage: Storage = localStorage) {
    if (storage.getItem(Constants.LocalStorageKeys.APP_STORAGE_RESET_VERSION) === Constants.FORCED_APP_STORAGE_RESET_VERSION) {
        return;
    }

    clearStoredArmoryData(storage);
    clearStoredUiState(storage);
    storage.removeItem(Constants.LocalStorageKeys.SAVED_LOADOUTS_STATE);
    storage.removeItem(Constants.LocalStorageKeys.ARMORY_STORAGE_MODEL);
    storage.removeItem(Constants.LocalStorageKeys.SCHEMA_VERSION);
    storage.setItem(Constants.LocalStorageKeys.APP_STORAGE_RESET_VERSION, Constants.FORCED_APP_STORAGE_RESET_VERSION);
}

function isSimulatorTab(tab: AppTabs) {
    return tab === AppTabs.ARGONAUT_LOADOUTS || tab === AppTabs.LOADOUT_STATS;
}

function getWorkspaceForTab(tab: AppTabs): WorkspaceView {
    if (isSimulatorTab(tab)) {
        return 'simulator';
    }

    return 'library';
}

function getQuickAssistCopy(workspace: WorkspaceView, activeTab: AppTabs, simulationMode: SimulationMode) {
    if (workspace === 'library') {
        return 'Manage custom items, storage files, and armory resets in one workspace. Pick an item on the left, then tune its stat cards on the right.';
    }

    if (simulationMode === 'compare') {
        return 'Compare loadouts by sweeping selected tokens across BP targets while keeping the results stage anchored below.';
    }

    return activeTab === AppTabs.ARGONAUT_LOADOUTS
        ? 'Use saved Titans, weapons, gear, and mnemos to test realistic builds while keeping the results stage in view.'
        : 'Manual runs stay flexible for edge cases. Swap input modes without resetting the battlefield or losing the results stage below.';
}

async function waitForSimulationKickoff(signal: AbortSignal): Promise<void> {
    throwIfSimulationAborted(signal);
    await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            signal.removeEventListener('abort', handleAbort);
            resolve();
        }, 10);

        const handleAbort = () => {
            clearTimeout(timeoutId);
            signal.removeEventListener('abort', handleAbort);
            reject(new DOMException('Simulation cancelled.', 'AbortError'));
        };

        signal.addEventListener('abort', handleAbort, { once: true });
    });
    throwIfSimulationAborted(signal);
}

const App: React.FC = () => {
    const [storageBootstrap] = useState(() => initializeAppStorage());
    const initialAppUiState = useMemo(() => loadAppUiState(), []);
    const initialSharedLoadout = useMemo<SharedLoadoutImportCandidate | null>(() => {
        return readSharedLoadoutFromHash(window.location.hash);
    }, []);
    const [simulationData, setSimulationData] = useState<SimulationOutput | null>(null);
    const [weaponMatrixData, setWeaponMatrixData] = useState<WeaponMatrixOutput | null>(null);
    const [isResultsLoading, setIsResultsLoading] = useState(false);
    const [pendingLoadoutTitles, setPendingLoadoutTitles] = useState<string[]>([]);
    const [pendingComparePlaceholder, setPendingComparePlaceholder] = useState<{
        loadoutTitles: string[];
        targets: WeaponMatrixInput['targets'];
    } | null>(null);
    const [isChangelogVisible, setIsChangelogVisible] = useState(false);
    const [showVersionMismatch] = useState(storageBootstrap.showWarning);
    const [schemaChangesMessage] = useState(storageBootstrap.schemaChangesMessage);
    const [workspace, setWorkspace] = useState<WorkspaceView>(() => {
        return getWorkspaceForTab(normalizeActiveTab(initialAppUiState.activeTab ?? null));
    });
    const [simulatorInputTab, setSimulatorInputTab] = useState<AppTabs>(() => {
        return normalizeSimulatorInputTab(initialAppUiState.simulatorInputTab ?? initialAppUiState.activeTab ?? null);
    });
    const [simulationMode, setSimulationMode] = useState<SimulationMode>(() => {
        return initialAppUiState.simulationMode === 'compare' ? 'compare' : 'standard';
    });
    const [pendingSharedLoadout, setPendingSharedLoadout] = useState<SharedLoadoutImportCandidate | null>(initialSharedLoadout);
    const [sharedLoadoutImportRequest, setSharedLoadoutImportRequest] = useState<SharedLoadoutImportRequest | null>(null);
    const [isGuideExpanded, setIsGuideExpanded] = useState(() => initialAppUiState.simulatorGuideExpanded ?? false);
    const [canCancelSimulation, setCanCancelSimulation] = useState(false);
    const activeRunControllerRef = useRef<AbortController | null>(null);
    const latestRunIdRef = useRef(0);

    const appVersion = process.env.REACT_APP_VERSION;
    const activeTab = useMemo(() => {
        if (workspace === 'simulator') {
            return simulatorInputTab;
        }

        return AppTabs.ARMORY_EDITOR;
    }, [simulatorInputTab, workspace]);
    const hasResults = simulationMode === 'compare' ? Boolean(weaponMatrixData) : Boolean(simulationData);
    const quickAssistCopy = getQuickAssistCopy(workspace, activeTab, simulationMode);
    const resultsStageTitle = simulationMode === 'compare' ? 'Loadout Analyzer Output' : 'Attack Simulator Output';

    const cancelSimulation = useCallback(() => {
        activeRunControllerRef.current?.abort();
    }, []);

    const startRun = useCallback(() => {
        activeRunControllerRef.current?.abort();
        const controller = new AbortController();
        const runId = latestRunIdRef.current + 1;
        latestRunIdRef.current = runId;
        activeRunControllerRef.current = controller;
        setCanCancelSimulation(true);
        setIsResultsLoading(true);

        return { controller, runId };
    }, []);

    const finishRun = useCallback((controller: AbortController) => {
        if (activeRunControllerRef.current === controller) {
            activeRunControllerRef.current = null;
            setCanCancelSimulation(false);
            setIsResultsLoading(false);
        }
    }, []);

    const handleSimulationSubmit = useCallback(async (input: SimulationInput) => {
        setPendingLoadoutTitles(input.loadouts.map((loadout, index) => loadout.name?.trim() || `Loadout ${index + 1}`));
        const { controller, runId } = startRun();

        try {
            await waitForSimulationKickoff(controller.signal);
            const result = await runSimulation(input, { signal: controller.signal });
            if (controller.signal.aborted || activeRunControllerRef.current !== controller || latestRunIdRef.current !== runId) {
                return;
            }

            setSimulationData(result);
            setWeaponMatrixData(null);
        } catch (error) {
            if (!isSimulationAbortedError(error)) {
                throw error;
            }
        } finally {
            finishRun(controller);
        }
    }, [finishRun, startRun]);

    const handleWeaponMatrixSubmit = useCallback(async (input: WeaponMatrixInput) => {
        setPendingComparePlaceholder({
            loadoutTitles: input.scenarios[0]?.loadouts.map((loadout, index) => loadout.name?.trim() || `Loadout ${index + 1}`) ?? [],
            targets: input.targets,
        });
        const { controller, runId } = startRun();

        try {
            await waitForSimulationKickoff(controller.signal);
            const result = await runWeaponMatrixSimulation(input, { signal: controller.signal });
            if (controller.signal.aborted || activeRunControllerRef.current !== controller || latestRunIdRef.current !== runId) {
                return;
            }

            setWeaponMatrixData(result);
            setSimulationData(null);
        } catch (error) {
            if (!isSimulationAbortedError(error)) {
                throw error;
            }
        } finally {
            finishRun(controller);
        }
    }, [finishRun, startRun]);

    const toggleChangelog = () => {
        setIsChangelogVisible(!isChangelogVisible);
    };

    const handleWorkspaceChange = (nextWorkspace: WorkspaceView) => {
        setWorkspace(nextWorkspace);
    };

    const handleActiveTabChange = (tab: AppTabs) => {
        if (isSimulatorTab(tab)) {
            setSimulatorInputTab(tab);
            if (workspace !== 'simulator') {
                setWorkspace('simulator');
            }
            return;
        }

        setWorkspace(getWorkspaceForTab(tab));
    };

    const clearPendingSharedLoadoutHash = useCallback(() => {
        const nextUrl = clearSharedLoadoutHash(window.location.href);
        window.history.replaceState(window.history.state, '', nextUrl);
    }, []);

    const getSharedLoadoutPreview = useCallback((candidate: SharedLoadoutImportCandidate): SharedLoadoutImportPreview => {
        const simulatorState = loadSimulatorState();
        const visibleTags = expandCycleSpoilerTags(simulatorState.visibleTags ?? Constants.DEFAULT_VISIBLE_SPOILERS);
        const manualMissingSpoilerTags = expandCycleSpoilerTags(getRequiredSpoilerTagsForLoadoutSnapshot(candidate.snapshot)).filter((tag) => !visibleTags.includes(tag));
        const armoryImportPreview = candidate.armoryRecipe
            ? previewSharedArmoryImport(candidate.armoryRecipe, loadEffectiveArmoryFromStorage(), candidate.title)
            : null;
        const armoryMissingSpoilerTags = armoryImportPreview
            ? expandCycleSpoilerTags([...manualMissingSpoilerTags, ...armoryImportPreview.requiredTags]).filter((tag) => !visibleTags.includes(tag))
            : [];

        return {
            armoryImportPreview,
            canImportAsArmory: Boolean(armoryImportPreview),
            manualMissingSpoilerTags,
            armoryMissingSpoilerTags,
        };
    }, []);

    const pendingSharedLoadoutPreview = useMemo(() => {
        return pendingSharedLoadout ? getSharedLoadoutPreview(pendingSharedLoadout) : null;
    }, [getSharedLoadoutPreview, pendingSharedLoadout]);

    const dismissSharedLoadout = useCallback(() => {
        setPendingSharedLoadout(null);
        setSharedLoadoutImportRequest(null);
        clearPendingSharedLoadoutHash();
    }, [clearPendingSharedLoadoutHash]);

    const stageSharedLoadoutImport = useCallback(
        (importTarget: 'manual' | 'armory', mode: SharedLoadoutImportMode) => {
            if (!pendingSharedLoadout || !pendingSharedLoadoutPreview) {
                return;
            }

            setSharedLoadoutImportRequest({
                requestId: `shared-loadout-${Date.now()}`,
                mode,
                importTarget,
                candidate: pendingSharedLoadout,
                missingSpoilerTags:
                    importTarget === 'armory'
                        ? pendingSharedLoadoutPreview.armoryMissingSpoilerTags
                        : pendingSharedLoadoutPreview.manualMissingSpoilerTags,
                ...(importTarget === 'armory' && pendingSharedLoadoutPreview.armoryImportPreview
                    ? { armoryImportPreview: pendingSharedLoadoutPreview.armoryImportPreview }
                    : {}),
            });
            setPendingSharedLoadout(null);
        },
        [pendingSharedLoadout, pendingSharedLoadoutPreview],
    );

    const handleSharedLoadoutImportApplied = useCallback(
        (requestId: string) => {
            setSharedLoadoutImportRequest((currentRequest) => {
                if (currentRequest?.requestId !== requestId) {
                    return currentRequest;
                }

                clearPendingSharedLoadoutHash();
                return null;
            });
        },
        [clearPendingSharedLoadoutHash],
    );

    useEffect(() => {
        saveAppUiState({
            activeTab,
            simulatorInputTab,
            simulationMode,
            simulatorGuideExpanded: isGuideExpanded,
        });
    }, [activeTab, isGuideExpanded, simulationMode, simulatorInputTab]);

    useEffect(() => {
        if (!isSimulatorTab(activeTab)) {
            cancelSimulation();
        }
    }, [activeTab, cancelSimulation]);

    useEffect(() => {
        cancelSimulation();
    }, [simulationMode, cancelSimulation]);

    useEffect(() => {
        return () => {
            cancelSimulation();
        };
    }, [cancelSimulation]);

    useEffect(() => {
        const handleHashChange = () => {
            setPendingSharedLoadout(readSharedLoadoutFromHash(window.location.hash));
            setSharedLoadoutImportRequest(null);
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    const handleOpenSharedLoadoutCandidate = useCallback((candidate: SharedLoadoutImportCandidate) => {
        setPendingSharedLoadout(candidate);
        setSharedLoadoutImportRequest(null);
    }, []);

    return (
        <div className="App app-shell">
            <div className="app-shell-background" />
            <header className="App-header app-header-shell">
                <div className="app-topbar">
                    <div className="app-brand">
                        <p className="app-eyebrow">Aeon Trespass: Odyssey</p>
                        <h1 className="header-title">Attack Simulator</h1>
                        <p className="app-subtitle">
                            <span>An unofficial fan-made tool for checking wound probability and comparing loadouts.</span>{' '}
                            <span className="app-subtitle-disclaimer">
                                Not affiliated with Into the Unknown Studios. Discuss in Discord{' '}
                                <a
                                    href="https://discord.com/channels/613726628559847445/1297857078089748552"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    here
                                </a>
                                .
                            </span>
                        </p>
                    </div>

                    <div className="app-header-meta">
                        <div className="version-number">Version: {appVersion}</div>
                        <div className="app-header-actions">
                            <Button className="header-utility-button" variant="ghost" onClick={toggleChangelog}>
                                Changelog
                            </Button>
                        </div>
                    </div>
                </div>

                {showVersionMismatch && (
                    <label className="schema-version-mismatch">
                        The format of your locally stored data (e.g. armory items) is outdated, which could lead to incorrect calculations. To fix this, go to the "Armory Library" workspace and use "Reset
                        Armory." If you'd like to resolve these issues manually, download your data first, review the schema changes below, then fix and re-upload your files.
                        <br />
                        {schemaChangesMessage}
                    </label>
                )}

                <SegmentedControl
                    ariaLabel="Workspace"
                    className="workspace-nav"
                    fullWidth
                    size="lg"
                    value={workspace}
                    onChange={handleWorkspaceChange}
                    options={[
                        { value: 'simulator', label: 'Simulator' },
                        { value: 'library', label: 'Armory Library' },
                    ]}
                />

                <section className={`workspace-help-card simulator-guide-card ${isGuideExpanded ? 'workspace-help-card-expanded' : ''}`}>
                        <div className="workspace-help-card-header">
                            <div className="workspace-help-copy">
                                <p className="workspace-card-title">Quick Assist</p>
                                <p className="workspace-card-body">{quickAssistCopy}</p>
                            </div>
                            <div className="workspace-help-actions">
                                <Button className="header-utility-button" variant="ghost" onClick={() => setIsGuideExpanded((current: boolean) => !current)}>
                                    {isGuideExpanded ? 'Hide Guide' : 'Open Guide'}
                                </Button>
                            </div>
                        </div>

                        {isGuideExpanded && <Welcome />}
                    </section>

                <main className="app-main-shell">
                    <Simulator
                        onSubmit={handleSimulationSubmit}
                        onMatrixSubmit={handleWeaponMatrixSubmit}
                        onCancelSimulation={cancelSimulation}
                        canCancelSimulation={canCancelSimulation}
                        activeTab={activeTab}
                        onActiveTabChange={handleActiveTabChange}
                        simulationMode={simulationMode}
                        onSimulationModeChange={setSimulationMode}
                        isSubmitting={isResultsLoading}
                        sharedLoadoutImport={sharedLoadoutImportRequest}
                        onSharedManualLoadoutImportApplied={handleSharedLoadoutImportApplied}
                        onOpenSharedLoadoutCandidate={handleOpenSharedLoadoutCandidate}
                    />

                    {workspace === 'simulator' && (
                        <section className="results-stage">
                            <div className="results-stage-header">
                                <p className="workspace-card-title">{resultsStageTitle}</p>
                            </div>

                            {activeTab === AppTabs.ARGONAUT_LOADOUTS || activeTab === AppTabs.LOADOUT_STATS ? (
                                <>
                                    {isResultsLoading ? (
                                        <div className="results-stage-stack" aria-busy="true">
                                            <div className={`results-stage-content ${simulationMode === 'compare' ? 'results-stage-content-loading' : ''}`}>
                                                {simulationMode === 'standard' ? (
                                                    <Histogram data={[]} aeonTrespassValue={1} placeholderTitles={pendingLoadoutTitles} isLoading />
                                                ) : (
                                                    <WeaponMatrixResults output={null} placeholder={pendingComparePlaceholder ?? undefined} isLoading />
                                                )}
                                            </div>
                                        </div>
                                    ) : null}

                                    {!isResultsLoading && simulationMode === 'standard' && simulationData && (
                                        <Histogram
                                            data={simulationData.loadoutOutputs}
                                            aeonTrespassValue={simulationData.primordial.aeonTrespassValue}
                                            aeonTrespassValues={simulationData.primordial.aeonTrespassValues}
                                        />
                                    )}
                                    {!isResultsLoading && simulationMode === 'compare' && weaponMatrixData && <WeaponMatrixResults output={weaponMatrixData} />}
                                    {!isResultsLoading && !hasResults && (
                                        <div className="results-empty-state">
                                            <h3>Run a simulation to populate this stage</h3>
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </section>
                    )}
                </main>
            </header>

            {isChangelogVisible && (
                <div className="changelog-overlay" onClick={toggleChangelog}>
                    <div className="changelog-modal" onClick={(e) => e.stopPropagation()}>
                        <Changelog />
                    </div>
                </div>
            )}

            {pendingSharedLoadout && (
                <SharedLoadoutImportModal
                    candidate={pendingSharedLoadout}
                    canImportAsArmory={pendingSharedLoadoutPreview?.canImportAsArmory ?? false}
                    manualMissingSpoilerTags={pendingSharedLoadoutPreview?.manualMissingSpoilerTags ?? []}
                    armoryMissingSpoilerTags={pendingSharedLoadoutPreview?.armoryMissingSpoilerTags ?? []}
                    onAddManual={() => stageSharedLoadoutImport('manual', 'add')}
                    onReplaceManual={() => stageSharedLoadoutImport('manual', 'replace')}
                    onAddArmory={() => stageSharedLoadoutImport('armory', 'add')}
                    onReplaceArmory={() => stageSharedLoadoutImport('armory', 'replace')}
                    onDismiss={dismissSharedLoadout}
                />
            )}
        </div>
    );
};

export default App;
